import { Component, inject } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacDialogLayoutComponent,
  CacFormBuilderComponent,
  formBuilder,
  ItemRecord,
} from '@controladad/ng-base';
import { AddressApiService } from '../../../services';
import { CustomerAddressEntity } from '@models';
import { formControl } from '@al00x/forms';
import { BehaviorSubject, switchMap, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';

export interface CustomerAddressDialogData {
  address?: CustomerAddressEntity;
  userId?: number;
}

export type CustomerAddressDialogResult = boolean;

@Component({
  selector: 'lib-customer-address-dialog',
  standalone: true,
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacButtonComponent],
  templateUrl: './customer-address-dialog.component.html',
  styleUrl: './customer-address-dialog.component.scss',
})
export class CustomerAddressDialogComponent extends CacBaseDialogComponent<
  CustomerAddressDialogData,
  CustomerAddressDialogResult
> {
  private addressApi = inject(AddressApiService);
  areaList$ = new BehaviorSubject<ItemRecord<number>[]>([]);

  form = formBuilder({
    cols: 3,
    width: '100%',
    inputs: {
      governorateId: {
        control: formControl<number>(this.data.address?.governorate.id, [Validators.required, Validators.min(1)]),
        label: 'Governorate',
        type: 'select',
        items: this.addressApi.getGovernorates(),
      },
      areaId: {
        control: formControl<number>(this.data.address?.area.id, [Validators.required, Validators.min(1)]),
        label: 'Area',
        type: 'select',
        items: this.areaList$,
      },
      address: {
        control: formControl<string>(this.data.address?.address, [Validators.required, Validators.maxLength(4096)]),
        label: 'Address',
        type: 'textarea',
      },
    },
    onInit: (controls) => {
      // Load areas for existing address on initialization
      if (this.data.address?.governorate.id) {
        this.addressApi.getAreas(this.data.address.governorate.id)
          .pipe(take(1))
          .subscribe(areas => this.areaList$.next(areas));
      }

      // Listen for changes to governorate
      controls.governorateId.valueChanges
        .pipe(
          takeUntilDestroyed(),
          switchMap((value) => {
            controls.areaId.setValue(0);
            if(!value) return [];
            return this.addressApi.getAreas(value).pipe(take(1));
          }),
          tap((areas) => this.areaList$.next(areas)),
        )
        .subscribe();
    },
  });

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.formGroup.getRawValue();
    if (this.data.userId)
      (this.data.address
        ? this.addressApi.update({
          updateFields: { ...values },
          addressId: this.data.address.id,
          customerId: this.data.userId,
        })
        : this.addressApi.create({
          fields: { ...values },
          customerId: this.data.userId,
        })
      )
        .pipe(e.pipe())
        .subscribe(() => {
          this.submit(true);
        });
  }

  onDelete(e: ButtonClickEvent) {
    const addressId = this.data.address?.id;
    const customerId = this.data.userId;
    if (!addressId || !customerId) return;

    this.addressApi
      .delete(customerId, addressId)
      .pipe(e.pipe())
      .subscribe(() => {
        this.submit(true);
        snackbar$.show('Delete Successful');
      });
  }
}
